import React, { useRef } from 'react'
import { Box, Button, Typography, Paper } from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'

interface ImageUploaderProps {
  label: string
  onImageUpload: (imageData: string, file: File) => void
  currentImage?: string
  disabled?: boolean
}

const ImageUploader: React.FC<ImageUploaderProps> = ({
  label,
  onImageUpload,
  currentImage,
  disabled = false
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Proszę wybrać plik obrazu')
      return
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024 // 10MB
    if (file.size > maxSize) {
      alert('Plik jest za duży. Maksymalny rozmiar to 10MB')
      return
    }

    // Read file as base64
    const reader = new FileReader()
    reader.onload = (e) => {
      const imageData = e.target?.result as string
      onImageUpload(imageData, file)
    }
    reader.readAsDataURL(file)
  }

  const handleClick = () => {
    fileInputRef.current?.click()
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>
      <Paper
        elevation={2}
        sx={{
          p: 3,
          textAlign: 'center',
          border: '2px dashed',
          borderColor: currentImage ? 'primary.main' : 'grey.400',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          transition: 'all 0.3s ease',
          '&:hover': {
            borderColor: disabled ? 'grey.400' : 'primary.main',
            bgcolor: disabled ? 'inherit' : 'action.hover'
          }
        }}
        onClick={!disabled ? handleClick : undefined}
      >
        {currentImage ? (
          <Box>
            <img
              src={currentImage}
              alt="Preview"
              style={{
                maxWidth: '100%',
                maxHeight: '200px',
                objectFit: 'contain'
              }}
            />
            <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
              Kliknij, aby zmienić obraz
            </Typography>
          </Box>
        ) : (
          <Box>
            <CloudUploadIcon sx={{ fontSize: 48, color: 'grey.500', mb: 1 }} />
            <Typography variant="body1" color="text.secondary">
              Kliknij, aby wybrać obraz
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Obsługiwane formaty: PNG, JPG, WebP, AVIF (max 10MB)
            </Typography>
          </Box>
        )}
      </Paper>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        style={{ display: 'none' }}
        disabled={disabled}
      />
    </Box>
  )
}

export default ImageUploader
